import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import {
  TGetPrizeBySlugForRedemptionResponse,
  TTokenRedemptionOptionPrize,
} from '@daymade/api-contracts';

import { tokenApiClient } from 'utils/apiClient';

import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';

import Separator from 'components/Separator';
import TokenRedemptionCostPill from '../TokenRedemptionCostPill';
import styles from './PrizeRedemptionModal.module.css';

import { PrizeType } from '@daymade/common-lib/dbEnums';
import Collapsible from 'components/layout/collapsible/Collapsible';
import {
  AddressForm,
  TAddress,
} from 'components/prizes/address-form/AddressForm';
import { Input } from 'components/prizes/input/Input';
import PrizeInputSection from 'components/prizes/prize-input-section/PrizeInputSection';
import RedemptionPrizeOption from 'components/prizes/redemption-prize-option/RedemptionPrizeOption';
import parse, { DOMNode, domToReact } from 'html-react-parser';
import HttpStatusCode from 'utils/HttpStatusCode';
import GenericButton from '../generic-button/GenericButton';
import PurchaseTokensMessage from '../purchase-tokens-message/PurchaseTokensMessage';
import ImageOverlay from '../shared/image-overlay/ImageOverlay';

interface PrizeRedemptionModalProps {
  tokenRedemptionOptionId: number;
  selectedPrize: TTokenRedemptionOptionPrize;
  tokenCost: number;
  closePopup: () => void;
  onSuccessfulChoiceHandler: (id: number) => Promise<void>;
}

type PrizeRedemptionProduct =
  PrizeRedemptionModalProps['selectedPrize']['products'][number];

const PrizeRedemptionModal = observer(
  ({
    tokenRedemptionOptionId,
    selectedPrize,
    tokenCost,
    closePopup,
    onSuccessfulChoiceHandler,
  }: PrizeRedemptionModalProps) => {
    const [selectedOption, setSelectedOption] =
      useState<PrizeRedemptionProduct | null>(
        selectedPrize.type === PrizeType.OPTION
          ? selectedPrize.products[0]
          : null,
      );

    const { balances } = useUserStore();

    const [confirmingPrizeChoice, setConfirmingPrizeChoice] = useState(false);

    const handleDismiss = () => closePopup();

    const handleChoosePrize = () => setConfirmingPrizeChoice(true);

    return (
      <div
        className={`popupWithDimmedBackground ${styles.container}`}
        id="dimmedBackgroundPrize"
        onClick={(e) => {
          if ((e.target as HTMLElement).id === 'dimmedBackgroundPrize') {
            handleDismiss();
          }
        }}
      >
        {confirmingPrizeChoice ? (
          <ConfirmPrizeView
            tokenCost={tokenCost ?? 0}
            {...{
              tokenRedemptionOptionId,
              selectedPrize,
              selectedOption,
              tokenBalance: balances,
              handleDismiss,
              onSuccessfulChoiceHandler,
            }}
          />
        ) : (
          <ChoosePrizeView
            {...{
              selectedPrize,
              selectedOption,
              setSelectedOption,
              onChoose: handleChoosePrize,
              handleDismiss,
              tokenCost,
              hasEnoughTokens: (balances?.tokenBalance ?? 0) >= tokenCost,
            }}
          />
        )}
      </div>
    );
  },
);

interface ChoosePrizeViewProps {
  selectedPrize: TTokenRedemptionOptionPrize;
  selectedOption: PrizeRedemptionProduct | null;
  tokenCost: number;
  hasEnoughTokens: boolean;
  setSelectedOption: (product: PrizeRedemptionProduct) => void;
  onChoose: () => void;
  handleDismiss: () => void;
}

const ChoosePrizeView = ({
  selectedPrize,
  selectedOption,
  tokenCost,
  hasEnoughTokens,
  setSelectedOption,
  onChoose,
  handleDismiss,
}: ChoosePrizeViewProps) => {
  return (
    <div className={styles.message} id="choosePrizeModal">
      <HeroSection
        heroBackgroundLink={selectedPrize.imageLocations.main}
        tokenCost={tokenCost}
        handleDismiss={handleDismiss}
      />
      <div className={styles.mainContainer}>
        <div>
          <p className={styles.selectedPrizeTitle}>{selectedPrize.title}</p>
          <p className={styles.selectedPrizeSubheading}>
            {selectedPrize.subheading}
          </p>
        </div>
        {selectedPrize.overviewBody &&
          selectedPrize.overviewBody
            .replace(/<p>(\s|&nbsp;)*<\/p>/g, '')
            .trim() && (
            <>
              <Separator margin={0} />
              <div className={`${styles.overviewBody} ${styles.bodyText}`}>
                {parse(selectedPrize.overviewBody, {
                  replace: (node) => {
                    if (node.type === 'tag' && node.name === 'p') {
                      return (
                        <p>
                          {domToReact(node.children as unknown as DOMNode[])}
                        </p>
                      );
                    }
                  },
                })}
              </div>
            </>
          )}
        {selectedPrize.whatsIncludedBody && (
          <>
            <Separator margin={0} />

            <Collapsible
              title={
                <p className={styles.selectedPrizeSubheading}>
                  What's Included?
                </p>
              }
            >
              <div style={{ paddingTop: 15 }}>
                {parse(selectedPrize.whatsIncludedBody, {
                  replace: (node) => {
                    if (node.type === 'tag' && node.name === 'p') {
                      return (
                        <p
                          className={`${styles.whatsIncluded} ${styles.bodyText}`}
                        >
                          {domToReact(node.children as unknown as DOMNode[])}
                        </p>
                      );
                    }

                    if (node.type === 'tag' && node.name === 'li') {
                      return (
                        <li className={`${styles.listItem} ${styles.bodyText}`}>
                          {domToReact(node.children as unknown as DOMNode[])}
                        </li>
                      );
                    }
                  },
                })}
              </div>

              {(selectedPrize.type === PrizeType.BUNDLE ||
                selectedPrize.type === PrizeType.MULTI_OPTION) && (
                <ul>
                  {selectedPrize.products.map((product) => (
                    <li
                      className={`${styles.listItem} ${styles.bodyText}`}
                      key={product.id}
                    >
                      {product.name}
                    </li>
                  ))}
                </ul>
              )}
            </Collapsible>
          </>
        )}
        {selectedPrize.type === PrizeType.OPTION && (
          <>
            <Separator margin={0} />

            <p
              className={styles.selectedPrizeSubheading}
              style={{ textAlign: 'left' }}
            >
              Select An Option
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              {selectedPrize.products.map((product) => (
                <RedemptionPrizeOption
                  key={product.id}
                  product={product}
                  onClick={() => setSelectedOption(product)}
                  selected={selectedOption?.id === product.id}
                />
              ))}
            </div>
          </>
        )}
        <div className={styles.buttonContainer}>
          <GenericButton
            onClick={onChoose}
            // ENG-394 - caused a compile error, probably never needed - disabledMessage={INSUFFICIENT_TOKENS_MESSAGE}
            disabled={
              !hasEnoughTokens ||
              (selectedPrize.type === PrizeType.OPTION && !selectedOption)
            }
          >
            REDEEM NOW FOR {tokenCost} TOKENS
          </GenericButton>
        </div>

        {!hasEnoughTokens ? <PurchaseTokensMessage /> : null}
      </div>
    </div>
  );
};

interface ConfirmPrizeViewProps {
  tokenRedemptionOptionId: number;
  tokenCost: number;
  selectedPrize: TTokenRedemptionOptionPrize;
  selectedOption:
    | PrizeRedemptionModalProps['selectedPrize']['products'][number]
    | null;
  onSuccessfulChoiceHandler: (id: number) => Promise<void>;
  handleDismiss: () => void;
}

const ConfirmPrizeView = observer(
  ({
    tokenRedemptionOptionId,
    tokenCost,
    selectedPrize,
    selectedOption,
    onSuccessfulChoiceHandler,
    handleDismiss,
  }: ConfirmPrizeViewProps) => {
    const { displayToast } = useUIStore();
    const { updateCurrentUser } = useUserStore();

    const [prizeChoiceError, setPrizeChoiceError] = useState<string | null>(
      null,
    );
    const [confirmLoading, setConfirmLoading] = useState(false);

    const [deliveryAddress, setDeliveryAddress] = useState<TAddress>();
    const [emailAddress, setEmailAddress] = useState<string>();
    const [phoneNumber, setPhoneNumber] = useState<string>();

    const [additionalQuestionsAnswers, setAdditionalQuestionsAnswers] =
      useState<
        TGetPrizeBySlugForRedemptionResponse['additionalQuestionsForPlayer']
      >(
        Object.fromEntries(
          Object.keys(selectedPrize.additionalQuestionsForPlayer).map((key) => [
            key,
            '',
          ]),
        ),
      );

    const handleConfirmPrize = async () => {
      setConfirmLoading(true);
      setPrizeChoiceError(null);

      const missingRequiredFields = [
        ...(selectedPrize.requiresDeliveryAddress
          ? Object.values(deliveryAddress ?? {})
          : []),
        ...(selectedPrize.requiresEmailAddress ? [emailAddress] : []),
        ...(selectedPrize.requiresPhoneNumber ? [phoneNumber] : []),
        ...Object.values(additionalQuestionsAnswers),
      ].some((v) => !v || v.trim() === '');

      if (missingRequiredFields) {
        setPrizeChoiceError(
          'Please fill out all fields before submitting your choice.',
        );
        setConfirmLoading(false);
        return;
      }

      const resp = await tokenApiClient.auth.redeemWithToken({
        params: { id: tokenRedemptionOptionId },
        body: {
          PRIZE: {
            prizeId: selectedPrize.id,
            productId: selectedOption?.id,
            deliveryStreet: deliveryAddress?.street,
            deliveryCity: deliveryAddress?.city,
            deliveryPostcode: deliveryAddress?.postcode,
            emailAddress,
            phoneNumber,
            additionalInfoFromPlayer: additionalQuestionsAnswers,
          },
        },
      });

      switch (resp.status) {
        case HttpStatusCode.OK:
          displayToast({
            title:
              'Great choice! We’ll be in touch via email within 14 working days to get you your prize.',
            timeout: 9000,
          });
          await onSuccessfulChoiceHandler(resp.body.id as unknown as number);

          break;

        case HttpStatusCode.BAD_REQUEST:
          handleDismiss();
          displayToast({
            title: resp.body.message,
            color: '#FF6600',
            timeout: 5000,
          });
          break;

        default:
          break;
      }

      setConfirmLoading(false);
      await updateCurrentUser();
    };

    return (
      <div className={styles.message} id="choosePrizeModal">
        <HeroSection
          heroBackgroundLink={
            selectedOption?.imageLocation ?? selectedPrize.imageLocations.main
          }
          tokenCost={tokenCost}
          handleDismiss={handleDismiss}
        />
        <div className={styles.mainContainer}>
          <div>
            <p
              style={{ textAlign: 'center', fontStyle: 'italic' }}
              className={styles.bodyText}
            >
              You've chosen
            </p>

            <p className={styles.selectedPrizeTitle}>
              {selectedPrize.title}{' '}
              {selectedOption?.name && `(${selectedOption.name})`}
            </p>
          </div>

          <Separator margin={0} />

          {/* Delivery / email / phone */}

          {(selectedPrize.requiresDeliveryAddress ||
            selectedPrize.requiresEmailAddress ||
            selectedPrize.requiresPhoneNumber ||
            Object.entries(selectedPrize.additionalQuestionsForPlayer).length >
              0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              {selectedPrize.requiresDeliveryAddress && (
                <PrizeInputSection
                  header={'Delivery Address'}
                  subText={
                    'Please enter the address where you would like your prize delivered'
                  }
                >
                  <AddressForm onChange={setDeliveryAddress} />
                </PrizeInputSection>
              )}

              {selectedPrize.requiresEmailAddress && (
                <PrizeInputSection
                  header={'Email Address'}
                  subText={
                    'Please enter your preferred email address to receive your prize'
                  }
                >
                  <Input
                    type="text"
                    required
                    placeholder="Type here.."
                    className={styles.inputTextBox}
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                  />
                </PrizeInputSection>
              )}

              {selectedPrize.requiresPhoneNumber && (
                <PrizeInputSection
                  header={'Phone Number'}
                  subText={
                    'Please enter your preferred number for delivery updates'
                  }
                >
                  <Input
                    type="tel"
                    placeholder="Type here.."
                    className={styles.inputTextBox}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </PrizeInputSection>
              )}

              {/* Additional questions */}
              {Object.entries(selectedPrize.additionalQuestionsForPlayer).map(
                ([question, description]) => (
                  <PrizeInputSection header={question} subText={description}>
                    <Input
                      type="text"
                      required
                      placeholder="Type here.."
                      className={styles.inputTextBox}
                      value={additionalQuestionsAnswers[question]}
                      onChange={(e) =>
                        setAdditionalQuestionsAnswers({
                          ...additionalQuestionsAnswers,
                          [question]: e.target.value,
                        })
                      }
                    />
                  </PrizeInputSection>
                ),
              )}
            </div>
          )}

          {/* Error / success messaging */}
          {prizeChoiceError && (
            <div style={{ width: '100%', fontWeight: 700, color: 'red' }}>
              {prizeChoiceError}
            </div>
          )}
          <div className={styles.buttonContainer}>
            <GenericButton
              // ENG-394 - caused a compile error, probably never needed - disabledMessage={INSUFFICIENT_TOKENS_MESSAGE}
              onClick={handleConfirmPrize}
              disabled={
                selectedPrize.type === PrizeType.OPTION && !selectedOption
              }
              loading={confirmLoading}
            >
              CONFIRM CHOICE
            </GenericButton>
          </div>
          <p className={styles.prizeFulfillmentText}>
            By confirming your prize choice you agree to us sharing your details
            with our prize fulfillment partner who will send and organise
            delivery of your prize.
          </p>
        </div>
      </div>
    );
  },
);

interface IHeroSectionProps {
  heroBackgroundLink: string;
  tokenCost: number;
  handleDismiss: () => void;
}

const HeroSection = ({
  heroBackgroundLink,
  handleDismiss,
  tokenCost,
}: IHeroSectionProps) => {
  return (
    <div
      className={styles.heroSection}
      style={{ backgroundImage: `url(${heroBackgroundLink})` }}
    >
      <TokenRedemptionCostPill redemptionCost={tokenCost} />
      <div
        className={`${styles.closeButton} arcadeFont`}
        onClick={handleDismiss}
      >
        X
      </div>
      <ImageOverlay />
    </div>
  );
};
export default PrizeRedemptionModal;
